/**
 * The transition dialogs: claim, submit (with its note, document and
 * GitHub links), the Owner's own completion, approve-or-edit, reject and
 * complete. Each names what will happen and confirms with the version read.
 */

import type { ReactNode } from 'react';

import type { ApiFailure, TaskView } from '../../api.js';
import { MarkdownField, useWorkspaceCopy } from '../../components.js';
import { ChoiceGroup, ConfirmDialog, TextField } from '../../ui.js';
import { mergeExpectationApplies } from './merge-expectation.js';
import type { TaskDialogFields } from './useTaskDialogFields.js';
import type { DialogKind } from './useTaskDialogs.js';

export function TaskTransitionDialogs({
  view,
  dialog,
  busy,
  actionFailure,
  failureAlert,
  closeDialog,
  act,
  fields,
  mayUseDataRoom,
  approvableTypes,
}: {
  readonly view: TaskView;
  readonly dialog: DialogKind;
  readonly busy: boolean;
  readonly actionFailure: ApiFailure | null;
  readonly failureAlert: ReactNode;
  readonly closeDialog: () => void;
  readonly act: (view: TaskView, kind: Exclude<DialogKind, null>) => void;
  readonly fields: TaskDialogFields;
  readonly mayUseDataRoom: boolean;
  readonly approvableTypes: readonly string[];
}): ReactNode {
  const copy = useWorkspaceCopy();
  const task = view.task;
  const {
    reason,
    setReason,
    priority,
    setPriority,
    requiresMerge,
    setRequiresMerge,
    editType,
    setEditType,
    editDifficulty,
    setEditDifficulty,
    editDescription,
    setEditDescription,
    issueUrl,
    setIssueUrl,
    pullRequestUrl,
    setPullRequestUrl,
    mergeUrl,
    setMergeUrl,
    submitDocumentId,
    setSubmitDocumentId,
    note,
    setNote,
    documentChoices,
  } = fields;

  return (
    <>
      <ConfirmDialog
        open={dialog === 'claim'}
        title={copy.dialogs.claimTitle}
        body={copy.dialogs.claimBody(task.reference)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.claimEffects}
        confirmLabel={copy.actions.claim}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'claim');
        }}
      />

      <ConfirmDialog
        open={dialog === 'submit'}
        title={copy.dialogs.submitTitle}
        body={copy.dialogs.submitBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.submitEffects}
        confirmLabel={copy.actions.submit}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'submit');
        }}
      >
        {mayUseDataRoom && documentChoices.length > 0 ? (
          <ChoiceGroup
            legend={copy.dialogs.submitDocumentLabel}
            hint={copy.dialogs.submitDocumentHint}
            value={submitDocumentId}
            onChange={setSubmitDocumentId}
            choices={[
              { value: '', label: copy.dialogs.submitDocumentNone },
              ...documentChoices.map((choice) => ({
                value: choice.id,
                label: choice.title,
              })),
            ]}
          />
        ) : null}
        <MarkdownField
          label={copy.dialogs.submitNoteLabel}
          hint={copy.dialogs.submitNoteHint}
          value={note}
          onChange={setNote}
          rows={6}
          maxLength={2000}
        />
        {/* Evidence, for every task type, all of it optional. */}
        <p
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          {copy.dialogs.submitEvidenceHint}
        </p>
        <TextField
          label={copy.dialogs.issueUrl}
          value={issueUrl}
          onChange={setIssueUrl}
          dir="ltr"
          type="url"
          placeholder="https://github.com/…"
        />
        <TextField
          label={copy.dialogs.pullRequestUrl}
          value={pullRequestUrl}
          onChange={setPullRequestUrl}
          dir="ltr"
          type="url"
          placeholder="https://github.com/…"
        />
        <TextField
          label={copy.dialogs.mergeUrl}
          value={mergeUrl}
          onChange={setMergeUrl}
          dir="ltr"
          type="url"
          hint={copy.dialogs.mergeUrlOptional}
          placeholder="https://github.com/…"
        />
        <p
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          {copy.task.githubUnverified}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'complete_own'}
        title={copy.dialogs.completeOwnTitle}
        body={copy.dialogs.completeOwnBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.completeOwnEffects}
        confirmLabel={copy.actions.completeOwn}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'complete_own');
        }}
      >
        <MarkdownField
          label={copy.dialogs.completeOwnNoteLabel}
          hint={copy.dialogs.completeOwnNoteHint}
          value={note}
          onChange={setNote}
          rows={5}
          maxLength={2000}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'approve'}
        title={copy.dialogs.approveTitle}
        body={copy.dialogs.approveBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.approveEffects}
        confirmLabel={copy.actions.approve}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'approve');
        }}
      >
        <ChoiceGroup
          legend={copy.dialogs.priorityLabel}
          value={priority}
          onChange={setPriority}
          error={
            priority === null && actionFailure?.kind === 'invalid'
              ? copy.dialogs.priorityMissing
              : null
          }
          choices={['P1', 'P2', 'P3', 'P4'].map((value) => ({
            value,
            label: copy.priorities[value] ?? value,
            definition: copy.dialogs.priorityDefinitions[value],
          }))}
        />
        <ChoiceGroup
          legend={copy.propose.typeLabel}
          value={editType}
          onChange={setEditType}
          inline
          choices={approvableTypes.map((value) => ({
            value,
            label: copy.types[value] ?? value,
          }))}
        />
        <ChoiceGroup
          legend={copy.propose.difficultyLabel}
          value={editDifficulty}
          onChange={setEditDifficulty}
          inline
          choices={['EXTRA_SMALL', 'SMALL', 'MEDIUM', 'LARGE', 'EXTRA_LARGE'].map(
            (value) => ({
              value,
              label: copy.difficulties[value] ?? value,
            }),
          )}
        />
        <MarkdownField
          label={copy.propose.descriptionLabel}
          hint={copy.dialogs.approveDescriptionHint}
          value={editDescription}
          onChange={setEditDescription}
          rows={8}
          maxLength={20000}
        />
        {mergeExpectationApplies(editType ?? task.taskType) ? (
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              font: 'var(--type-body-md)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-md)',
              minHeight: 44,
            }}
          >
            <input
              type="checkbox"
              checked={requiresMerge}
              onChange={(event) => {
                setRequiresMerge(event.target.checked);
              }}
              style={{ width: 18, height: 18, accentColor: 'var(--mj-teal-600)' }}
            />
            {copy.dialogs.requiresMergeLabel}
          </label>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'reject'}
        title={copy.dialogs.rejectTitle}
        body={copy.dialogs.rejectBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.rejectEffects}
        confirmLabel={copy.actions.reject}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        danger
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'reject');
        }}
      >
        <TextField
          label={copy.dialogs.reasonLabel}
          value={reason}
          onChange={setReason}
          error={
            reason.trim().length === 0 && actionFailure?.kind === 'invalid'
              ? copy.dialogs.reasonRequired
              : null
          }
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'complete'}
        title={copy.dialogs.completeTitle}
        body={copy.dialogs.completeBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.completeEffects}
        confirmLabel={copy.actions.complete}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'complete');
        }}
      />
    </>
  );
}
